<template>
  <div id="feedback">
    <v-container fluid>
      <v-flex xs12>
        <HowToUseThisPage />
        <div class="feedback-container">
          <div class="route-container mb-4 section-heading">
            <h1>
              <CodeIcon :color="iconColor" class="feedback-icon" /> FEEDBACK
            </h1>
          </div>
          <!-- Display Feedback -->
          <div class="feedback-table mt-14">
            <v-card-title class="px-0 pb-6 d-flex align-center">
              <v-select
                v-model="selectedCategory"
                :items="categoryOptions"
                label="Filter by Category"
                variant="solo"
                density="compact"
                flat
                class="mr-3"
                style="max-width: 200px"
                hide-details="auto"
              ></v-select>

              <v-select
                v-model="selectedStatus"
                :items="statusOptions"
                label="Filter by Status"
                variant="solo"
                density="compact"
                flat
                class="mr-3"
                style="max-width: 200px"
                hide-details="auto"
              ></v-select>

              <v-btn
                @click="clearFilters"
                class="mx-3"
                color="primary"
                variant="text"
              >
                Clear Filters
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                class="mr-3 export-btn"
                color="primary"
                variant="outlined"
                prepend-icon="mdi-download"
                @click="exportFeedback"
              >
                Export CSV
              </v-btn>
              <v-text-field
                v-model="search"
                :append-inner-icon="search ? '' : 'mdi-magnify'"
                label="Search"
                single-line
                hide-details
                clearable
                variant="solo"
                flat
                density="compact"
                class="search-input pb-0 pr-3"
                :style="{ backgroundColor: editorBackgroundColor }"
                :color="editorBackgroundColor"
              ></v-text-field>
            </v-card-title>
            <v-data-table
              :headers="feedbackHeaders"
              fixed-header
              :items="filteredFeedback"
              :items-per-page="10"
              :footer-props="{ 'items-per-page-options': [10, 20, 30, 40, 50] }"
              :loading="isLoading"
              loading-text="Loading Feedback... Please wait"
              no-data-text="No Feedback Submissions"
              class="elevation-1 feedback-table mt-4 clickable-row"
              :search="search"
              :sort-by="['createdAt']"
            >
              <!-- Custom Headers -->
              <template #headers>
                <tr>
                  <th class="text-left" @click="setSort('name')">
                    Name
                    <v-icon size="small">
                      {{
                        sortBy === 'name'
                          ? sortDesc
                            ? 'mdi-chevron-down'
                            : 'mdi-chevron-up'
                          : 'mdi-chevron-up'
                      }}
                    </v-icon>
                  </th>
                  <th class="text-left" @click="setSort('email')">
                    Email
                    <v-icon size="small">
                      {{
                        sortBy === 'email'
                          ? sortDesc
                            ? 'mdi-chevron-down'
                            : 'mdi-chevron-up'
                          : 'mdi-chevron-up'
                      }}
                    </v-icon>
                  </th>
                  <th class="text-left" @click="setSort('category')">
                    Category
                    <v-icon size="small">
                      {{
                        sortBy === 'category'
                          ? sortDesc
                            ? 'mdi-chevron-down'
                            : 'mdi-chevron-up'
                          : 'mdi-chevron-up'
                      }}
                    </v-icon>
                  </th>
                  <th class="text-left" @click="setSort('rating')">
                    Rating
                    <v-icon size="small">
                      {{
                        sortBy === 'rating'
                          ? sortDesc
                            ? 'mdi-chevron-down'
                            : 'mdi-chevron-up'
                          : 'mdi-chevron-up'
                      }}
                    </v-icon>
                  </th>
                  <th class="text-left" @click="setSort('status')">
                    Status
                    <v-icon size="small">
                      {{
                        sortBy === 'status'
                          ? sortDesc
                            ? 'mdi-chevron-down'
                            : 'mdi-chevron-up'
                          : 'mdi-chevron-up'
                      }}
                    </v-icon>
                  </th>
                  <th class="text-left" @click="setSort('createdAt')">
                    Submitted
                    <v-icon size="small">
                      {{
                        sortBy === 'createdAt'
                          ? sortDesc
                            ? 'mdi-chevron-down'
                            : 'mdi-chevron-up'
                          : 'mdi-chevron-up'
                      }}
                    </v-icon>
                  </th>
                </tr>
              </template>

              <!-- Custom Row Rendering -->
              <template #item="{ item }">
                <tr @click="openFeedbackDetails(item)" style="cursor: pointer">
                  <td>{{ item.name }}</td>
                  <td>{{ item.email }}</td>
                  <td>
                    <v-chip
                      :color="getCategoryColor(item.category)"
                      size="small"
                      variant="flat"
                    >
                      {{ formatCategory(item.category) }}
                    </v-chip>
                  </td>
                  <td>
                    <v-rating
                      :model-value="item.rating"
                      readonly
                      density="compact"
                      size="small"
                      color="#A5D867"
                    />
                  </td>
                  <td>
                    <v-chip
                      :color="getStatusColor(item.status)"
                      size="small"
                      variant="flat"
                      :class="getStatusClass(item.status)"
                    >
                      {{ formatStatus(item.status) }}
                    </v-chip>
                  </td>
                  <td>
                    {{
                      item.createdAt?.seconds
                        ? new Date(
                            item.createdAt.seconds * 1000
                          ).toLocaleString()
                        : item.createdAt
                        ? new Date(item.createdAt).toLocaleString()
                        : 'N/A'
                    }}
                  </td>
                </tr>
              </template>

              <!-- Loading bar -->
              <template #progress>
                <v-progress-linear
                  color="#592892"
                  indeterminate
                ></v-progress-linear>
              </template>
            </v-data-table>
          </div>

          <!-- Feedback Details Dialog -->
          <v-dialog v-model="dialog" max-width="900px" scrollable>
            <v-card>
              <v-card-title
                class="feedback-dialog-title d-flex align-center justify-space-between"
              >
                <span>Feedback Details</span>
                <v-btn variant="text" icon @click="dialog = false">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-card-title>

              <v-card-text class="feedback-dialog-content">
                <div v-if="selectedFeedback" class="feedback-details">
                  <!-- Basic Info -->
                  <v-row class="mb-0">
                    <v-col cols="12" md="6" class="py-1">
                      <div class="detail-item">
                        <strong>Name:</strong> {{ selectedFeedback.name }}
                      </div>
                    </v-col>
                    <v-col cols="12" md="6" class="py-1">
                      <div class="detail-item">
                        <strong>Email:</strong> {{ selectedFeedback.email }}
                      </div>
                    </v-col>
                    <v-col cols="12" md="6" class="py-1">
                      <div class="detail-item">
                        <strong>Category:</strong>
                        <v-chip
                          :color="getCategoryColor(selectedFeedback.category)"
                          size="small"
                          variant="flat"
                          class="ml-2"
                        >
                          {{ formatCategory(selectedFeedback.category) }}
                        </v-chip>
                      </div>
                    </v-col>
                    <v-col cols="12" md="6" class="py-1">
                      <div class="detail-item">
                        <strong>Rating:</strong>
                        <v-rating
                          :model-value="selectedFeedback.rating"
                          readonly
                          density="compact"
                          size="small"
                          color="#A5D867"
                          class="ml-2"
                        />
                      </div>
                    </v-col>
                    <v-col cols="12" md="6" class="py-1">
                      <div class="detail-item">
                        <div class="mb-1">
                          <strong>Status:</strong>
                        </div>
                        <v-select
                          v-model="selectedFeedback.status"
                          :items="statusOptions"
                          variant="outlined"
                          density="compact"
                          style="max-width: 200px"
                          @update:model-value="updateStatus"
                        ></v-select>
                      </div>
                    </v-col>
                    <v-col cols="12" md="6" class="py-1">
                      <div class="detail-item">
                        <strong>Submitted:</strong>
                        {{
                          selectedFeedback.createdAt?.seconds
                            ? new Date(
                                selectedFeedback.createdAt.seconds * 1000
                              ).toLocaleString()
                            : selectedFeedback.createdAt
                            ? new Date(
                                selectedFeedback.createdAt
                              ).toLocaleString()
                            : 'N/A'
                        }}
                      </div>
                    </v-col>
                  </v-row>

                  <!-- Description -->
                  <v-row class="mb-0 mt-1">
                    <v-col cols="12">
                      <div class="detail-item">
                        <strong>Description:</strong>
                        <div
                          class="mt-1 pa-2"
                          style="background: #f5f5f5; border-radius: 4px"
                        >
                          {{ selectedFeedback.description }}
                        </div>
                      </div>
                    </v-col>
                  </v-row>

                  <!-- Screenshot -->
                  <v-row v-if="selectedFeedback.screenshot" class="mb-1">
                    <v-col cols="12">
                      <div class="detail-item">
                        <strong>Screenshot:</strong>
                        <div class="mt-1">
                          <img
                            :src="selectedFeedback.screenshot"
                            alt="Screenshot"
                            style="
                              max-width: 100%;
                              border-radius: 4px;
                              border: 1px solid #ddd;
                            "
                          />
                        </div>
                      </div>
                    </v-col>
                  </v-row>

                  <!-- System Information -->
                  <v-row class="mb-1">
                    <v-col cols="12">
                      <v-expansion-panels variant="accordion">
                        <v-expansion-panel>
                          <v-expansion-panel-title>
                            <strong>System Information</strong>
                          </v-expansion-panel-title>
                          <v-expansion-panel-text>
                            <div class="system-info">
                              <div>
                                <strong>Browser:</strong>
                                {{ selectedFeedback.browser }}
                              </div>
                              <div>
                                <strong>OS:</strong> {{ selectedFeedback.os }}
                              </div>
                              <div>
                                <strong>Screen Resolution:</strong>
                                {{ selectedFeedback.screenResolution }}
                              </div>
                              <div>
                                <strong>Viewport Size:</strong>
                                {{ selectedFeedback.viewportSize }}
                              </div>
                              <div>
                                <strong>Device Type:</strong>
                                {{ selectedFeedback.deviceType }}
                              </div>
                              <div>
                                <strong>Touch Support:</strong>
                                {{ selectedFeedback.hasTouch }}
                              </div>
                              <div>
                                <strong>Zoom Level:</strong>
                                {{ selectedFeedback.zoomLevel }}
                              </div>
                              <div>
                                <strong>Timezone:</strong>
                                {{ selectedFeedback.timezone }}
                              </div>
                              <div>
                                <strong>Theme:</strong>
                                {{ selectedFeedback.theme }}
                              </div>
                              <div>
                                <strong>Language:</strong>
                                {{ selectedFeedback.language }}
                              </div>
                              <div v-if="selectedFeedback.pageContext">
                                <strong>Page Context:</strong>
                                {{ selectedFeedback.pageContext }}
                              </div>
                              <div>
                                <strong>Page:</strong>
                                <a
                                  :href="selectedFeedback.page"
                                  target="_blank"
                                  >{{ selectedFeedback.page }}</a
                                >
                              </div>
                              <div>
                                <strong>Route:</strong>
                                {{ selectedFeedback.route }}
                              </div>
                            </div>
                          </v-expansion-panel-text>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </v-col>
                  </v-row>
                </div>
              </v-card-text>
              <v-card-actions class="feedback-dialog-actions">
                <v-btn color="primary" @click="dialog = false">Close</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <v-snackbar
            v-model="snackbar"
            color="#592892"
            :timeout="timeout"
            location="top"
          >
            <span v-html="snackbarMsg"></span>
            <template #actions="{ attrs }">
              <v-btn
                variant="text"
                v-bind="attrs"
                @click="snackbar = false"
                class="font-weight-bold"
              >
                Close
              </v-btn>
            </template>
          </v-snackbar>
        </div>
      </v-flex>
    </v-container>
  </div>
</template>

<script>
  import db from '@/firebase/init'
  import HowToUseThisPage from '@/components/utilities/HowToUseThisPage'
  import { mapGetters } from 'vuex'
  import CodeIcon from '@/assets/icons/CodeIcon.vue'
  import { currentTheme } from '@/plugins/vuetify'

  export default {
    name: 'Feedback',
    components: {
      HowToUseThisPage,
      CodeIcon,
    },
    data() {
      return {
        snackbar: false,
        snackbarMsg: '',
        search: '',
        timeout: 6000,
        isLoading: false,
        feedback: [],
        feedbackHeaders: [
          { text: 'Name', value: 'name' },
          { text: 'Email', value: 'email' },
          { text: 'Category', value: 'category' },
          { text: 'Rating', value: 'rating' },
          { text: 'Status', value: 'status' },
          { text: 'Submitted', value: 'createdAt' },
        ],
        dialog: false,
        selectedFeedback: null,
        selectedCategory: 'All Categories',
        selectedStatus: 'All Statuses',
        categoryOptions: ['All Categories', 'bug', 'feature', 'general'],
        statusOptions: ['All Statuses', 'new', 'reviewed', 'resolved'],
        sortBy: 'createdAt',
        sortDesc: true,
      }
    },
    computed: {
      ...mapGetters('UserData', ['isAuthenticated']),
      iconColor() {
        return currentTheme.value === 'dark' ? '#cecece' : '#383935'
      },
      editorBackgroundColor() {
        return currentTheme.value === 'dark' ? '#272727' : '#ffffff'
      },
      filteredFeedback() {
        let result = this.feedback.filter((item) => {
          const matchesCategory =
            this.selectedCategory === 'All Categories' ||
            item.category === this.selectedCategory

          const matchesStatus =
            this.selectedStatus === 'All Statuses' ||
            item.status === this.selectedStatus

          return matchesCategory && matchesStatus
        })

        if (this.sortBy) {
          result.sort((a, b) => {
            const valA = this.deepValue(a, this.sortBy)
            const valB = this.deepValue(b, this.sortBy)

            if (valA < valB) return this.sortDesc ? 1 : -1
            if (valA > valB) return this.sortDesc ? -1 : 1
            return 0
          })
        }

        return result
      },
    },
    async created() {
      this.isLoading = true
      await this.fetchFeedback()
    },
    methods: {
      deepValue(obj, path) {
        return path.split('.').reduce((o, p) => o?.[p], obj)
      },
      setSort(field) {
        if (this.sortBy === field) {
          this.sortDesc = !this.sortDesc
        } else {
          this.sortBy = field
          this.sortDesc = true
        }
      },
      async fetchFeedback() {
        try {
          this.isLoading = true
          const feedbackRef = db
            .collection('feedback')
            .orderBy('createdAt', 'desc')
          const snapshot = await feedbackRef.get()

          this.feedback = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))

          // Ensure status exists (default to 'new' for old submissions)
          this.feedback = this.feedback.map((item) => ({
            ...item,
            status: item.status || 'new',
          }))
        } catch (error) {
          console.error('Error fetching feedback:', error)
          this.snackbarMsg = 'Error loading feedback submissions'
          this.snackbar = true
        } finally {
          this.isLoading = false
        }
      },
      openFeedbackDetails(item) {
        this.selectedFeedback = { ...item }
        this.dialog = true
      },
      async updateStatus() {
        if (!this.selectedFeedback || !this.selectedFeedback.id) return

        try {
          await db.collection('feedback').doc(this.selectedFeedback.id).update({
            status: this.selectedFeedback.status,
          })

          // Update local feedback array
          const index = this.feedback.findIndex(
            (f) => f.id === this.selectedFeedback.id
          )
          if (index !== -1) {
            this.feedback[index].status = this.selectedFeedback.status
          }

          this.snackbarMsg = 'Status updated successfully'
          this.snackbar = true
        } catch (error) {
          console.error('Error updating status:', error)
          this.snackbarMsg = 'Error updating status'
          this.snackbar = true
        }
      },
      clearFilters() {
        this.selectedCategory = 'All Categories'
        this.selectedStatus = 'All Statuses'
        this.search = ''
      },
      formatCategory(category) {
        const categories = {
          bug: 'Bug',
          feature: 'Feature Request',
          general: 'General Feedback',
        }
        return categories[category] || category
      },
      formatStatus(status) {
        const statuses = {
          new: 'New',
          reviewed: 'Reviewed',
          resolved: 'Resolved',
        }
        return statuses[status] || status
      },
      getCategoryColor(category) {
        const colors = {
          bug: 'error',
          feature: '#A5D867',
          general: '#592892',
        }
        return colors[category] || 'default'
      },
      getStatusColor(status) {
        const colors = {
          new: 'warning',
          reviewed: 'info',
          resolved: '#A5D867',
        }
        return colors[status] || 'default'
      },
      getStatusClass(status) {
        return status === 'resolved' ? 'status-resolved' : ''
      },
      getSearchFilteredFeedback() {
        if (!this.search) {
          return [...this.filteredFeedback]
        }

        const term = this.search.toLowerCase()
        return this.filteredFeedback.filter((item) => {
          const values = [
            item.name,
            item.email,
            this.formatCategory(item.category),
            item.rating,
            this.formatStatus(item.status),
            item.description,
            item.page,
            item.route,
            item.pageContext,
          ]

          return values.some((value) =>
            value?.toString().toLowerCase().includes(term)
          )
        })
      },
      exportFeedback() {
        const dataToExport = this.getSearchFilteredFeedback()

        if (!dataToExport.length) {
          this.snackbarMsg = 'No feedback entries to export'
          this.snackbar = true
          return
        }

        const headers = [
          'Name',
          'Email',
          'Category',
          'Rating',
          'Status',
          'Submitted',
          'Description',
          'Has Screenshot',
          'Browser',
          'OS',
          'Screen Resolution',
          'Viewport Size',
          'Device Type',
          'Touch Support',
          'Zoom Level',
          'Timezone',
          'Theme',
          'Language',
          'Page Context',
          'Page',
          'Route',
        ]

        const rows = dataToExport.map((item) => [
          item.name || '',
          item.email || '',
          this.formatCategory(item.category) || '',
          item.rating ?? '',
          this.formatStatus(item.status) || '',
          item.createdAt?.seconds
            ? new Date(item.createdAt.seconds * 1000).toISOString()
            : item.createdAt
            ? new Date(item.createdAt).toISOString()
            : '',
          (item.description || '').replace(/\r?\n|\r/g, ' ').trim(),
          item.screenshot ? 'Yes' : 'No',
          item.browser || '',
          item.os || '',
          item.screenResolution || '',
          item.viewportSize || '',
          item.deviceType || '',
          item.hasTouch || '',
          item.zoomLevel || '',
          item.timezone || '',
          item.theme || '',
          item.language || '',
          item.pageContext || '',
          item.page || '',
          item.route || '',
        ])

        const csvContent = [headers, ...rows]
          .map((row) =>
            row
              .map((value) => {
                const stringValue = value?.toString() ?? ''
                if (
                  stringValue.includes('"') ||
                  stringValue.includes(',') ||
                  stringValue.includes('\n')
                ) {
                  return `"${stringValue.replace(/"/g, '""')}"`
                }
                return stringValue
              })
              .join(',')
          )
          .join('\n')

        const blob = new Blob([csvContent], {
          type: 'text/csv;charset=utf-8;',
        })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
          'download',
          `feedback-export-${new Date().toISOString().slice(0, 19)}.csv`
        )
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)

        this.snackbarMsg = 'Feedback exported successfully'
        this.snackbar = true
      },
    },
  }
</script>

<style scoped>
  .feedback-icon {
    margin-right: 10px;
  }

  .detail-item {
    margin-bottom: 4px;
  }

  .system-info {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 12px;
  }

  .system-info div {
    padding: 4px 0;
  }

  /* Fix expansion panel padding and tighten spacing in dialog */
  .feedback-dialog-title {
    padding: 10px 20px 6px 20px !important;
  }

  .feedback-dialog-content {
    padding: 6px 20px !important;
  }

  .feedback-dialog-actions {
    padding: 6px 20px 12px 20px !important;
  }

  .feedback-details :deep(.v-expansion-panel-title) {
    padding-left: 16px !important;
    padding-top: 12px !important;
    padding-bottom: 12px !important;
  }

  .feedback-details :deep(.v-expansion-panel-text__wrapper) {
    padding: 12px 16px 16px 16px !important;
  }

  .clickable-row {
    cursor: pointer;
  }

  .clickable-row tbody tr:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  .v-theme--dark .clickable-row tbody tr:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }

  .search-input {
    flex: 0 0 260px;
    max-width: 260px;
  }

  .status-resolved {
    color: #383935 !important;
  }
</style>
